"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Login = void 0;
const crypto_1 = __importDefault(require("crypto"));
const url_search_params_1 = __importDefault(require("@ungap/url-search-params"));
const APIErrors_1 = require("./resources/APIErrors");
const APITasks_1 = require("./resources/APITasks");
const APIAbortController_1 = require("./APIAbortController");
const APIError_1 = require("./APIError");
const FSIServerClientUtils_1 = require("./FSIServerClientUtils");
const SHA256_1 = require("./SHA256");
const modeNode = FSIServerClientUtils_1.FSIServerClientUtils.GET_MODE_NODE();
const URLSearchParams = url_search_params_1.default;
class Login {
    constructor(classInit, taskController) {
        this.classInit = classInit;
        this.taskController = taskController;
        this.sha256 = new SHA256_1.SHA256();
        this.client = classInit.client;
        this.com = classInit.com;
        this.translations = this.client.getTranslations();
        this.serviceURL = this.client.getService('login');
        this.servicePath = this.client.getServicePath('login');
    }
    authenticate(username, password, options = {}) {
        APIAbortController_1.APIAbortController.THROW_IF_ABORTED(options.abortController);
        const self = this;
        let bHTTPS = false;
        return this.com.getResponse(this.servicePath, { def: APIErrors_1.APIErrors.login, content: [username] }, undefined, options)
            .then(async (response) => {
            const responseURL = (typeof (response.config.url) === "string") ? response.config.url : "";
            APIAbortController_1.APIAbortController.THROW_IF_ABORTED(options.abortController);
            bHTTPS = responseURL.toUpperCase().indexOf("HTTPS://") === 0;
            if (modeNode) {
                self.extractSessionCookie(response.headers);
            }
            APIAbortController_1.APIAbortController.THROW_IF_ABORTED(options.abortController);
            const map = response.data;
            if (typeof (map) !== "object") {
                throw this.com.err.get(APIErrors_1.APIErrors.login, [username], APIErrors_1.APIErrors.invalidServerReply);
            }
            if (typeof (map.salt) !== "string" || map.state !== "success") {
                if (map.message && typeof (map.message) === "string") {
                    throw this.com.err.get(APIErrors_1.APIErrors.login, [username], APIErrors_1.APIErrors.serverError, [map.message]);
                }
                else {
                    throw this.com.err.get(APIErrors_1.APIErrors.login, [username], APIErrors_1.APIErrors.invalidServerReply);
                }
            }
            let hashedPassword;
            if (options.sendPlainLoginPassword) {
                map.loginmethod = "plain";
            }
            if (map.loginmethod !== "plain") {
                hashedPassword = await this.hash(map.salt + await this.hash(password));
            }
            else {
                if (!bHTTPS) {
                    if (this.client.getPromptFunction()) {
                        const promptOptions = {
                            fnPrompt: this.client.getPromptFunction()
                        };
                        const task = this.com.taskSupplier.get(APITasks_1.APITasks.sendPlainPassword, []);
                        const res = await this.com.prompt(promptOptions, task, "login.plainPassword", [
                            "no",
                            "yes",
                        ], this.taskController);
                        if (res.reply !== "yes") {
                            throw this.com.err.get(APIErrors_1.APIErrors.userAborted);
                        }
                    }
                    else {
                        throw this.com.err.get(APIErrors_1.APIErrors.login, [username], APIErrors_1.APIErrors.serverRefusedPlainPassword);
                    }
                }
                hashedPassword = password;
            }
            const query = new URLSearchParams();
            query.set("username", username);
            if (hashedPassword !== null) {
                query.set("password", hashedPassword);
            }
            APIAbortController_1.APIAbortController.THROW_IF_ABORTED(options.abortController);
            return this.com.postJSON(this.servicePath, query, {
                content: [username],
                def: APIErrors_1.APIErrors.login
            }, null, options)
                .then(bodySend => {
                APIAbortController_1.APIAbortController.THROW_IF_ABORTED(options.abortController);
                if (bodySend.state === 'success') {
                    this.com.onLoginChange(true);
                    const test1 = self.sha256.sha256Digest(username);
                    const test2 = self.sha256.sha256Digest(password);
                    const pattern = "8c6976e5b5410415bde908bd4dee15dfb167a9c873fc4bb8a81f6f2ab448a918";
                    if (test1 === pattern && test2 === pattern) {
                        bodySend.defaultPassword = true;
                    }
                    this.client.setCurrentUser(username);
                    return bodySend;
                }
                else {
                    throw this.com.err.get(APIErrors_1.APIErrors.login, [username], APIErrors_1.APIErrors.serverError, [bodySend.message]);
                }
            });
        });
    }
    logout(options = {}) {
        APIAbortController_1.APIAbortController.THROW_IF_ABORTED(options.abortController);
        return this.com.getJSON(this.client.getServicePath('logout'), { def: APIErrors_1.APIErrors.logout }, undefined, options)
            .then(body => {
            APIAbortController_1.APIAbortController.THROW_IF_ABORTED(options.abortController);
            if (typeof (body) === "object" && body.value === true) {
                this.com.onLoginChange(false);
                this.client.setCurrentUser("");
                return true;
            }
            else {
                throw this.com.err.get(APIErrors_1.APIErrors.logout, undefined, APIErrors_1.APIErrors.invalidServerReply);
            }
        });
    }
    getSha256Hash(str) {
        return this.hash(str);
    }
    changePassword(currentPassword, newPassWord, options = {}) {
        const self = this;
        const url = this.client.getServicePath("password");
        let oldShaPass;
        let newShaPass;
        return new Promise((resolve, reject) => {
            this.com.getJSON(url, { def: APIErrors_1.APIErrors.changePassWord }, undefined, options)
                .then(async (res) => {
                if (res && res.salt) {
                    oldShaPass = await this.getSha256Hash(res.salt
                        + await this.getSha256Hash(currentPassword));
                    newShaPass = await this.getSha256Hash(newPassWord);
                }
                else {
                    throw (self.com.err.get(APIErrors_1.APIErrors.changePassWord, undefined, APIErrors_1.APIErrors.invalidServerReply));
                }
                this.com.putJsonBoolean(url, {
                    "newPasswordHash": newShaPass,
                    "oldPasswordHash": oldShaPass
                }, { def: APIErrors_1.APIErrors.changePassWord, content: [] }, null, options)
                    .then(resolve)
                    .catch(reject);
            })
                .catch(err => {
                reject(err);
            });
        });
    }
    changeUser(user, options = {}) {
        const self = this;
        return new Promise((resolve, reject) => {
            this.com.putJsonBoolean(this.client.getServicePath("user/change"), user, { def: APIErrors_1.APIErrors.changeUser, content: [user] }, null, options)
                .then(res => {
                if (res) {
                    self.client.setCurrentUser(user);
                    resolve(res);
                }
            })
                .catch(err => {
                if (err instanceof APIError_1.APIError) {
                    reject(err);
                }
                else {
                    reject(err);
                }
            });
        });
    }
    getUserList(options = {}) {
        return this.com.getJSON(this.client.getServicePath("user/list"), { def: APIErrors_1.APIErrors.getUserList }, undefined, options);
    }
    async hash(str) {
        let ret;
        if (modeNode) {
            try {
                ret = crypto_1.default.createHash('sha256').update(str, 'utf8').digest('hex');
            }
            catch (err) {
                ret = null;
            }
        }
        else {
            try {
                const msgUint8 = new window.TextEncoder().encode(str);
                const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                ret = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            }
            catch (err) {
                ret = null;
            }
        }
        if (ret === null) {
            ret = this.sha256.hash(str);
            if (!ret) {
                ret = "";
            }
        }
        return ret;
    }
    extractSessionCookie(responseHeaders) {
        const cookies = responseHeaders['set-cookie'] || [];
        const sessionCookie = cookies.map((entry) => {
            return entry.replace(/;.*/, "");
        }).join(';');
        if (sessionCookie) {
            this.client.setSessionCookie(sessionCookie);
        }
        return true;
    }
}
exports.Login = Login;
